import { IconButton as MuiIconButton, styled, SvgIcon } from '@mui/material'
import { ForwardedRef, forwardRef, MouseEventHandler, ReactNode } from 'react'

type IconButtonProps = {
    disabled?: boolean
    icon: ReactNode
    size?: 'small' | 'medium'
    onClick?: MouseEventHandler<HTMLButtonElement>
    ariaLabel?: string
    color?: string
    hoverColor?: string
    label?: string
}

const StyledMuiIconButton = styled(MuiIconButton, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color?: string; $hoverColor?: string }>(({ theme, $color, $hoverColor }) => ({
    padding: 0,
    flexDirection: 'column',
    color: $color || theme.palette.primary1.blue100,
    ...theme.typography.subtitle1,
    '&:hover': {
        color: $hoverColor || theme.palette.primary2.green100,
    },
    '&:disabled': {
        color: theme.palette.primary1.blue50,
    },
}))

const SIZE_CONFIGURATION = {
    small: {
        padding: '8px',
        margin: '-8px',
        height: '16px',
        width: '16px',
        boxSizing: 'content-box',
    },
    medium: {
        padding: '10px',
        margin: '-10px',
        height: '20px',
        width: '20px',
        boxSizing: 'content-box',
    },
}

const IconButton = forwardRef(
    (
        { disabled = false, icon, size = 'medium', onClick, ariaLabel, label, color, hoverColor, ...props }: IconButtonProps,
        ref: ForwardedRef<HTMLButtonElement>
    ) => {
        return (
            <StyledMuiIconButton
                ref={ref}
                disabled={disabled}
                disableRipple
                onClick={onClick}
                aria-label={ariaLabel}
                $color={color}
                $hoverColor={hoverColor}
                {...props}
            >
                <SvgIcon sx={{ ...SIZE_CONFIGURATION[size] }} className={disabled ? 'disabled' : undefined}>
                    {icon}
                </SvgIcon>
                {label && <span style={{ height: '14px' }}>{label}</span>}
            </StyledMuiIconButton>
        )
    }
)

export default IconButton
